import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingCart, Heart, Search, Sun, Moon, Menu, User, LogOut, Store, LayoutDashboard, Settings, MapPin, ChevronDown, Package, Bell, Coins, MessageSquare, CreditCard, Ticket, LifeBuoy, RotateCcw, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { useCurrency } from "@/context/CurrencyContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { COUNTRIES } from "@/lib/countries";

const NAVY = "#131921";
const NAVY_2 = "#232F3E";

export const Header = () => {
  const { user, logout } = useAuth();
  const { cartCount, wishlist } = useCart();
  const { t, lang, setLang, langs, currentFlag } = useI18n();
  const { theme, toggle } = useTheme();
  const { currency, setCurrency, currencies } = useCurrency();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [menuOpen, setMenuOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locOpen, setLocOpen] = useState(false);
  const [city, setCity] = useState(() => localStorage.getItem("xm_city") || "");
  const [cityDraft, setCityDraft] = useState("");
  const [country, setCountry] = useState(() => localStorage.getItem("xm_country") || "NG");
  const [region, setRegion] = useState(() => localStorage.getItem("xm_state") || "");
  const [countryDraft, setCountryDraft] = useState("NG");
  const [regionDraft, setRegionDraft] = useState("");
  const [langDraft, setLangDraft] = useState(lang);
  const [currencyDraft, setCurrencyDraft] = useState(currency);
  const navigate = useNavigate();

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  // First-visit country detection: preselect the visitor's country + currency if they haven't chosen yet.
  useEffect(() => {
    if (localStorage.getItem("xm_country")) return;
    let cancelled = false;
    fetch("https://ipwho.is/?fields=country_code")
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const cc = d && d.country_code ? String(d.country_code).toUpperCase() : "";
        const match = COUNTRIES.find((c) => c.code === cc);
        if (!match) return;
        setCountry(match.code);
        localStorage.setItem("xm_country", match.code);
        if (!localStorage.getItem("xm_currency")) {
          setCurrency(match.code === "NG" ? "NGN" : match.code === "US" ? "USD" : "EUR");
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "seller" ? "/seller" : "/account";
  const doSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (cat && cat !== "all") params.set("category", cat);
    navigate(`/products?${params.toString()}`);
  };
  const go = (to) => { setMenuOpen(false); setAllOpen(false); navigate(to); };
  const openPrefs = () => {
    setCountryDraft(country || "NG"); setRegionDraft(region); setCityDraft(city);
    setLangDraft(lang); setCurrencyDraft(currency); setLocOpen(true);
  };
  const savePrefs = () => {
    setCountry(countryDraft); localStorage.setItem("xm_country", countryDraft);
    setRegion(regionDraft); localStorage.setItem("xm_state", regionDraft);
    const c = cityDraft.trim().slice(0, 60); setCity(c); localStorage.setItem("xm_city", c);
    if (langDraft !== lang) setLang(langDraft);
    if (currencyDraft !== currency) setCurrency(currencyDraft);
    setLocOpen(false);
  };
  const prefCountry = COUNTRIES.find((c) => c.code === countryDraft) || COUNTRIES[0];
  const prefStates = Object.keys(prefCountry.states);
  const prefCities = prefCountry.states[regionDraft] || [];
  const headerCountry = COUNTRIES.find((c) => c.code === (country || "NG")) || COUNTRIES[0];

  const [acctOpen, setAcctOpen] = useState(false);
  const navClose = (to) => { setAcctOpen(false); navigate(to); };
  const acctGo = (to) => navClose(user ? to : "/login");
  const isApprovedSeller = user?.role === "seller" && user?.seller_status === "approved";
  const myAccountItems = [
    { key: "orders", label: "My Orders", icon: Package, to: "/account?section=orders" },
    { key: "coins", label: "Xurray Coins", icon: Coins, to: "/account?section=wallet" },
    { key: "messages", label: "Messages", icon: MessageSquare, to: "/account?section=messages" },
    { key: "payments", label: "Payment", icon: CreditCard, to: "/account?section=payments" },
    { key: "wishlist", label: "Wish List", icon: Heart, to: "/wishlist" },
    { key: "coupons", label: "My Coupons", icon: Ticket, to: "/account?section=coupons" },
  ];
  const quickLinkItems = [
    ...(!isApprovedSeller ? [{ key: "sell", label: "Sell on Xurray", icon: Store, to: "/sell", auth: false }] : []),
    { key: "settings", label: "Settings", icon: Settings, to: "/settings", auth: true },
    ...(isApprovedSeller ? [{ key: "seller", label: "Seller Dashboard", icon: Store, to: "/seller", auth: true }] : []),
    { key: "help", label: "Help Center", icon: LifeBuoy, to: "/customer-service", auth: false },
    { key: "returns", label: "Return & Refund Policy", icon: RotateCcw, to: "/return-refund-policy", auth: false },
    { key: "disputes", label: "Disputes & Reports", icon: ShieldAlert, to: "/disputes", auth: false },
  ];

  const quickLinks = [
    ["/products?category=food-grocery", "Food & Grocery"],
    ["/products?category=womens-clothing", "Women's Clothing"],
    ["/products?category=mens-clothing", "Men's Clothing"],
    ["/products?category=wigs-accessories", "Wigs & Accessories"],
    ["/products?category=mens-shoes", "Shoes"],
    ["/products?category=bags", "Bags"],
    ["/products?category=cosmetics", "Beauty"],
    ["/products?flash=1", t("flashSales")],
  ];

  const mobileNav = [
    ["/", "Home"], ["/products", t("shop")], ["/products?flash=1", t("flashSales")],
    ["/products?category=food-grocery", "Food & Grocery"], ["/products?category=womens-clothing", "Women's Clothing"],
    ["/products?category=cosmetics", t("beauty")], ["/products?category=wine-alcohol", t("wineDrinks")],
  ];

  const accountLabel = user ? `Hello, ${user.name?.split(" ")[0] || "there"}` : "Hello, sign in";

  return (
    <header className="sticky top-0 z-50">
      {/* ===== MOBILE / TABLET HEADER ===== */}
      <div className="md:hidden backdrop-blur-xl bg-background/90 border-b border-border/60">
        <div className="px-3 flex items-center gap-2 h-14">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="shrink-0"><Menu className="w-6 h-6" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <SheetHeader><SheetTitle className="font-display font-black text-xl text-left">Xurray<span className="text-primary">Mart</span></SheetTitle><SheetDescription className="sr-only">Main navigation menu</SheetDescription></SheetHeader>
              <nav className="mt-6 flex flex-col gap-0.5 text-sm font-medium">
                {mobileNav.map(([to, label]) => (
                  <button key={to} data-testid={`mobile-nav-${label}`} onClick={() => go(to)} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent transition-colors">{label}</button>
                ))}
                <button onClick={() => go("/sell")} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent text-primary font-semibold transition-colors">{t("sellOnXurray")}</button>
                <div className="border-t my-2" />
                <button onClick={() => go("/wishlist")} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors"><Heart className="w-4 h-4" />Wishlist</button>
                {user && <button data-testid="mobile-following-link" onClick={() => go("/following")} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors"><Bell className="w-4 h-4" />Stores You Follow</button>}
                <button onClick={() => { toggle(); }} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors">{theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}{theme === "dark" ? "Light mode" : "Dark mode"}</button>
                {user ? (
                  <>
                    <button data-testid="mobile-dashboard-link" onClick={() => go(dashPath)} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors">{user.role === "buyer" ? <User className="w-4 h-4" /> : <LayoutDashboard className="w-4 h-4" />}{user.role === "buyer" ? t("profileTab") : t("dashboard")}</button>
                    <button data-testid="mobile-settings-link" onClick={() => go("/settings")} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors"><Settings className="w-4 h-4" />{t("settings") || "Settings"}</button>
                    <button onClick={() => { logout(); go("/"); }} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors"><LogOut className="w-4 h-4" />{t("logout")}</button>
                  </>
                ) : (
                  <button onClick={() => go("/login")} className="text-left px-3 py-2.5 rounded-lg hover:bg-accent flex items-center gap-2 transition-colors"><User className="w-4 h-4" />{t("login")}</button>
                )}
              </nav>
            </SheetContent>
          </Sheet>
          <Link to="/" className="flex items-center gap-1.5 shrink-0"><img src="/xurray-x-mark.png" alt="Xurray Mart" className="h-7 w-auto" /><span className="font-display font-black text-lg leading-none">Xurray<span className="text-primary">Mart</span></span></Link>
          <div className="flex items-center gap-0.5 ml-auto">
            <button data-testid="header-location-mobile" onClick={openPrefs}
              className="shrink-0 flex items-center gap-1 px-1.5 py-1 rounded-md hover:bg-accent transition-colors">
              <span className="text-base leading-none">{headerCountry.flag}</span>
              <span className="text-[11px] font-bold">{currency}</span>
            </button>
            {!user && <Button data-testid="header-login-btn-mobile" onClick={() => navigate("/login")} variant="ghost" size="icon"><User className="w-5 h-5" /></Button>}
            {user && <Button data-testid="user-menu-mobile" onClick={() => navigate(dashPath)} variant="ghost" size="icon"><User className="w-5 h-5" /></Button>}
            <Link to="/cart" className="relative"><Button variant="ghost" size="icon"><ShoppingCart className="w-5 h-5" /></Button>{cartCount > 0 && <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] bg-primary">{cartCount}</Badge>}</Link>
          </div>
        </div>
        <form onSubmit={doSearch} className="px-3 pb-2.5 flex items-center relative">
          <Input data-testid="search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pr-12 h-10 rounded-full bg-muted/50" />
          <Button type="submit" size="icon" className="absolute right-4 h-8 w-8 rounded-full bg-primary text-white"><Search className="w-4 h-4" /></Button>
        </form>
      </div>

      {/* ===== DESKTOP AMAZON-STYLE HEADER ===== */}
      <div className="hidden md:block">
        {/* Top bar */}
        <div style={{ backgroundColor: NAVY }} className="text-white">
          <div className="max-w-[1600px] mx-auto px-3 flex items-center gap-1.5 h-[60px]">
            <Link to="/" data-testid="logo-link" className="flex items-center gap-1.5 shrink-0 px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors">
              <img src="/xurray-x-mark.png" alt="Xurray Mart" className="h-8 w-auto" />
              <span className="font-display font-black text-lg leading-none">Xurray<span className="text-primary">Mart</span></span>
            </Link>

            {/* Ship-to / preferences */}
            <button data-testid="header-location" onClick={openPrefs}
              className="hidden lg:flex items-center gap-1.5 shrink-0 px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors text-left">
              <span className="text-xl leading-none" data-testid="header-country-flag">{headerCountry.flag}</span>
              <span className="leading-tight">
                <span className="block text-[11px] text-white/70">Deliver to {region || headerCountry.name}</span>
                <span className="block text-sm font-bold -mt-0.5" data-testid="header-currency-code">{headerCountry.name} · {currency}</span>
              </span>
            </button>

            {/* Search with category dropdown */}
            <form onSubmit={doSearch} className="flex-1 flex items-stretch h-10 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-primary">
              <select data-testid="search-category-select" value={cat} onChange={(e) => setCat(e.target.value)}
                aria-label="Select a category to search"
                className="bg-[#e6e6e6] hover:bg-[#dcdcdc] text-black text-xs px-2 border-r border-gray-300 max-w-[120px] cursor-pointer outline-none">
                <option value="all">All</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
                className="flex-1 h-10 rounded-none border-0 bg-white text-black focus-visible:ring-0 focus-visible:ring-offset-0" />
              <button data-testid="search-btn" type="submit" aria-label="Search" className="w-11 bg-primary hover:bg-primary/90 flex items-center justify-center text-black">
                <Search className="w-5 h-5" />
              </button>
            </form>

            {/* Account & Lists */}
            <DropdownMenu open={acctOpen} onOpenChange={setAcctOpen}>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-account" className="shrink-0 px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors text-left leading-tight">
                  <span className="block text-[11px] text-white/80">{accountLabel}</span>
                  <span className="flex items-center text-sm font-bold -mt-0.5">Account & Lists <ChevronDown className="w-3 h-3 ml-0.5 opacity-70" /></span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0 overflow-hidden" data-testid="account-menu">
                {/* Top section */}
                <div className="p-4 bg-muted/40 border-b">
                  {user ? (
                    <div data-testid="account-welcome">
                      <p className="text-xs text-muted-foreground">Welcome back</p>
                      <p className="font-bold text-base truncate">{user.name}</p>
                      <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary capitalize">
                        {user.role}{isApprovedSeller ? " · approved" : ""}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <p className="font-bold text-sm mb-2.5">Welcome to Xurray Mart</p>
                      <div className="flex gap-2">
                        <Button data-testid="dropdown-signin-btn" onClick={() => navClose("/login")} className="flex-1 rounded-full h-9">Sign In</Button>
                        <Button data-testid="dropdown-register-btn" onClick={() => navClose("/register")} variant="outline" className="flex-1 rounded-full h-9">Register</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* My Account */}
                <div className="px-2 py-2">
                  <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">My Account</p>
                  <div className="grid grid-cols-3 gap-1">
                    {myAccountItems.map((it) => {
                      const Icon = it.icon;
                      const count = it.key === "wishlist" && wishlist?.length > 0 ? wishlist.length : null;
                      return (
                        <button key={it.key} data-testid={`acct-${it.key}`} onClick={() => acctGo(it.to)}
                          className="relative flex flex-col items-center gap-1.5 rounded-lg p-2.5 hover:bg-accent transition-colors text-center">
                          <Icon className="w-5 h-5 text-primary" />
                          <span className="text-[11px] leading-tight">{it.label}</span>
                          {count != null && <span className="absolute top-1 right-2 text-[9px] bg-primary text-white rounded-full px-1">{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <DropdownMenuSeparator />

                {/* Quick Links */}
                <div className="px-2 py-2">
                  <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Quick Links</p>
                  {quickLinkItems.map((it) => {
                    const Icon = it.icon;
                    return (
                      <button key={it.key} data-testid={`quick-${it.key}`}
                        onClick={() => (it.auth ? acctGo(it.to) : navClose(it.to))}
                        className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm hover:bg-accent transition-colors text-left">
                        <Icon className="w-4 h-4 text-muted-foreground" />{it.label}
                      </button>
                    );
                  })}
                </div>

                {user && (
                  <>
                    <DropdownMenuSeparator />
                    <button data-testid="logout-btn" onClick={() => { setAcctOpen(false); logout(); navigate("/"); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium hover:bg-accent transition-colors text-left">
                      <LogOut className="w-4 h-4" />{t("logout")}
                    </button>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Orders */}
            <button data-testid="header-orders" onClick={() => navigate(user ? (user.role === "buyer" ? "/account?section=orders" : dashPath) : "/login")}
              className="hidden lg:block shrink-0 px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors text-left leading-tight">
              <span className="block text-[11px] text-white/80">Returns</span>
              <span className="block text-sm font-bold -mt-0.5">& Orders</span>
            </button>

            {/* Basket */}
            <Link to="/cart" data-testid="cart-link" className="shrink-0 flex items-end gap-1 px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors relative">
              <div className="relative">
                <ShoppingCart className="w-7 h-7" />
                <span data-testid="cart-count" className="absolute -top-1.5 left-3.5 text-primary font-black text-sm">{cartCount}</span>
              </div>
              <span className="text-sm font-bold mb-0.5">Basket</span>
            </Link>
          </div>
        </div>

        {/* Second bar */}
        <div style={{ backgroundColor: NAVY_2 }} className="text-white">
          <div className="max-w-[1600px] mx-auto px-2 flex items-center gap-1 h-[39px] text-sm">
            <Sheet open={allOpen} onOpenChange={setAllOpen}>
              <SheetTrigger asChild>
                <button data-testid="header-all-menu" className="flex items-center gap-1.5 font-bold px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors">
                  <Menu className="w-5 h-5" />All
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0 overflow-y-auto">
                <SheetTitle className="sr-only">Shop by category</SheetTitle>
                <SheetDescription className="sr-only">Browse all product categories</SheetDescription>
                <div style={{ backgroundColor: NAVY }} className="text-white px-5 py-4 flex items-center gap-2 cursor-pointer" data-testid="all-menu-greeting" onClick={() => go(user ? "/account" : "/login")}>
                  <User className="w-5 h-5" /><span className="font-bold text-lg">{user ? `Hello, ${user.name?.split(" ")[0]}` : "Hello, sign in"}</span>
                </div>
                <div className="py-2">
                  <p className="px-5 py-2 font-bold text-sm">Shop by category</p>
                  {categories.map((c) => (
                    <button key={c.slug} data-testid={`all-cat-${c.slug}`} onClick={() => go(`/products?category=${c.slug}`)} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent flex items-center justify-between transition-colors">
                      {c.name}
                    </button>
                  ))}
                  <div className="border-t my-2" />
                  <button onClick={() => go("/products")} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent transition-colors">All products</button>
                  <button onClick={() => go("/products?flash=1")} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent transition-colors">{t("flashSales")}</button>
                  <button onClick={() => go("/sell")} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent text-primary font-semibold transition-colors">{t("sellOnXurray")}</button>
                  <div className="border-t my-2" />
                  <p className="px-5 py-2 font-bold text-sm">Help &amp; Settings</p>
                  {!user && (
                    <button data-testid="all-menu-signin" onClick={() => go("/login")} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent flex items-center gap-2 transition-colors"><User className="w-4 h-4" />Sign in</button>
                  )}
                  {user && (
                    <button data-testid="all-menu-account" onClick={() => go("/account")} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent flex items-center gap-2 transition-colors"><User className="w-4 h-4" />Your account</button>
                  )}
                  <button data-testid="all-menu-settings" onClick={() => go("/settings")} className="w-full text-left px-5 py-2.5 text-sm hover:bg-accent flex items-center gap-2 transition-colors"><Settings className="w-4 h-4" />Settings</button>
                </div>
              </SheetContent>
            </Sheet>
            {quickLinks.map(([to, label]) => (
              <button key={to} onClick={() => go(to)} className="px-2 py-1.5 rounded border border-transparent hover:border-white/60 transition-colors whitespace-nowrap">{label}</button>
            ))}
            <button data-testid="nav-sell" onClick={() => go("/sell")} className="ml-auto px-2 py-1.5 rounded font-semibold text-primary hover:brightness-110 whitespace-nowrap transition-colors">{t("sellOnXurray")}</button>
          </div>
        </div>
      </div>

      {/* Location & preferences */}
      <Dialog open={locOpen} onOpenChange={setLocOpen}>
        <DialogContent className="max-w-md bg-white text-gray-900 p-0 overflow-hidden" data-testid="prefs-modal">
          <DialogHeader className="px-6 pt-6 pb-1">
            <DialogTitle className="flex items-center gap-2 text-gray-900"><MapPin className="w-5 h-5 text-emerald-500" />Location &amp; preferences</DialogTitle>
            <DialogDescription className="text-gray-500">Choose where to ship, your language and currency.</DialogDescription>
          </DialogHeader>
          <div className="px-6 pb-2 space-y-5 max-h-[60vh] overflow-y-auto">
            {/* Ship to */}
            <div className="space-y-2">
              <p className="text-sm font-bold text-gray-900">Ship to</p>
              <select data-testid="pref-country" value={countryDraft} aria-label="Country"
                onChange={(e) => { setCountryDraft(e.target.value); setRegionDraft(""); setCityDraft(""); }}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 bg-white text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
              </select>
              <select data-testid="pref-state" value={regionDraft} aria-label="State or region"
                onChange={(e) => { setRegionDraft(e.target.value); setCityDraft(""); }}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 bg-white text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                <option value="">Select state / region</option>
                {prefStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select data-testid="pref-city" value={cityDraft} disabled={!regionDraft} aria-label="City"
                onChange={(e) => setCityDraft(e.target.value)}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 bg-white text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:text-gray-400">
                <option value="">{regionDraft ? "Select city" : "Select a region first"}</option>
                {prefCities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Language */}
            <div className="space-y-2">
              <p className="text-sm font-bold text-gray-900">Language</p>
              <select data-testid="pref-language" value={langDraft} aria-label="Language" onChange={(e) => setLangDraft(e.target.value)}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 bg-white text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                {langs.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
            </div>
            {/* Currency */}
            <div className="space-y-2">
              <p className="text-sm font-bold text-gray-900">Currency</p>
              <select data-testid="pref-currency" value={currencyDraft} aria-label="Currency" onChange={(e) => setCurrencyDraft(e.target.value)}
                className="w-full h-11 rounded-lg border border-gray-300 px-3 bg-white text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                {currencies.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code} · {c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="px-6 py-4 border-t bg-white">
            <Button data-testid="prefs-save-btn" onClick={savePrefs} className="w-full h-11 rounded-full bg-black text-white hover:bg-black/90">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
